import Phaser from "phaser";
import { GameConfig } from "@/config/GameConfig";
import { createButton } from "@/ui/Button";
import { buildHowToPlayCards } from "@/ui/howToPlayContent";
import { hexToCss } from "@/ui/colors";

const SHEET_WIDTH = 440;
const SHEET_MAX_HEIGHT = 560;
const SHEET_PADDING = 22;
const TITLE_AREA_HEIGHT = 52;
const CLOSE_AREA_HEIGHT = 70;
const CARD_GAP = 18;

export interface HowToPlayOverlay {
  container: Phaser.GameObjects.Container;
  setVisible(visible: boolean): void;
  /** Call after the scene resizes - repositions the sheet, re-wraps text, and re-clamps scroll. */
  relayout(): void;
  destroy(): void;
}

/**
 * A single, reusable "HOW TO PLAY" overlay: a scrollable sheet (drag or
 * wheel) with three compact cards (SAFE/RUSH/CHAOS), built from
 * `howToPlayContent` so the numbers can never drift from `GameConfig`.
 * Used identically by MenuScene, and by GameScene's Pause menu and
 * in-game help button - one implementation, one source of truth.
 */
export function createHowToPlayOverlay(scene: Phaser.Scene, depth: number, onClose: () => void): HowToPlayOverlay {
  const container = scene.add.container(0, 0).setDepth(depth).setVisible(false);

  const dim = scene.add.rectangle(0, 0, 10, 10, 0x0a0c12, 0.82);
  container.add(dim);

  const sheetBg = scene.add.graphics();
  container.add(sheetBg);

  const title = scene.add
    .text(0, 0, "HOW TO PLAY", {
      fontFamily: "Arial, sans-serif",
      fontSize: "22px",
      fontStyle: "bold",
      color: "#f5f7fa",
    })
    .setOrigin(0.5, 0);
  container.add(title);

  const contentContainer = scene.add.container(0, 0);
  container.add(contentContainer);

  const closeButton = createButton(scene, 0, 0, "CLOSE", () => onClose(), {
    width: 180,
    height: 46,
    fillColor: 0x1c2030,
    hoverColor: 0x262c40,
    textColor: "#f5f7fa",
  });
  container.add(closeButton);

  // A transparent drag handle spanning the scrollable viewport - simpler
  // and cheaper to reposition on resize than making every text object
  // itself draggable.
  const dragZone = scene.add.zone(0, 0, 10, 10).setOrigin(0, 0);
  container.add(dragZone);

  const cards = buildHowToPlayCards();
  let maskShape: Phaser.GameObjects.Graphics | undefined;
  let scrollY = 0;
  let maxScroll = 0;
  let viewportTop = 0;
  let viewportHeight = 0;
  let sheetWidth = 0;

  let dragging = false;
  let dragStartPointerY = 0;
  let dragStartScroll = 0;

  const onPointerDown = (pointer: Phaser.Input.Pointer) => {
    dragging = true;
    dragStartPointerY = pointer.y;
    dragStartScroll = scrollY;
  };
  const onPointerMove = (pointer: Phaser.Input.Pointer) => {
    if (!dragging || maxScroll <= 0) return;
    scrollY = Phaser.Math.Clamp(dragStartScroll - (pointer.y - dragStartPointerY), 0, maxScroll);
    contentContainer.y = viewportTop - scrollY;
  };
  const onPointerUp = () => {
    dragging = false;
  };
  const onWheel = (_pointer: unknown, _objects: unknown, _dx: number, dy: number) => {
    if (!container.visible || maxScroll <= 0) return;
    scrollY = Phaser.Math.Clamp(scrollY + dy * 0.5, 0, maxScroll);
    contentContainer.y = viewportTop - scrollY;
  };

  dragZone.setInteractive();
  dragZone.on("pointerdown", onPointerDown);
  scene.input.on("pointermove", onPointerMove);
  scene.input.on("pointerup", onPointerUp);
  scene.input.on("pointerupoutside", onPointerUp);
  scene.input.on("wheel", onWheel);

  function buildCardTexts(): number {
    contentContainer.removeAll(true);
    const innerWidth = sheetWidth - SHEET_PADDING * 2;
    let y = 0;
    for (const card of cards) {
      const header = scene.add
        .text(0, y, card.title, {
          fontFamily: "Arial, sans-serif",
          fontSize: "16px",
          fontStyle: "bold",
          color: hexToCss(card.color),
        })
        .setOrigin(0, 0);
      contentContainer.add(header);
      y += header.height + 6;

      for (const line of card.lines) {
        const lineText = scene.add
          .text(0, y, line, {
            fontFamily: "Arial, sans-serif",
            fontSize: "13px",
            color: "#d7dbe6",
            wordWrap: { width: innerWidth },
          })
          .setOrigin(0, 0);
        contentContainer.add(lineText);
        y += lineText.height + 5;
      }

      const blurb = scene.add
        .text(0, y, card.blurb, {
          fontFamily: "Arial, sans-serif",
          fontSize: "12px",
          fontStyle: "italic",
          color: "#8b93a7",
          wordWrap: { width: innerWidth },
        })
        .setOrigin(0, 0);
      contentContainer.add(blurb);
      y += blurb.height + CARD_GAP;
    }
    return y;
  }

  function relayout(): void {
    const { width, height } = scene.scale;

    dim.setSize(width, height);
    dim.setPosition(width / 2, height / 2);

    sheetWidth = Math.min(SHEET_WIDTH, width * 0.92);
    const sheetHeight = Math.min(SHEET_MAX_HEIGHT, height * 0.86);
    const sheetX = width / 2 - sheetWidth / 2;
    const sheetTop = height / 2 - sheetHeight / 2;

    sheetBg.clear();
    sheetBg.fillStyle(0x171a24, 1);
    sheetBg.fillRoundedRect(sheetX, sheetTop, sheetWidth, sheetHeight, 18);
    sheetBg.lineStyle(1.5, GameConfig.colors.gridLine, 1);
    sheetBg.strokeRoundedRect(sheetX, sheetTop, sheetWidth, sheetHeight, 18);

    title.setPosition(width / 2, sheetTop + 16);

    viewportTop = sheetTop + TITLE_AREA_HEIGHT;
    viewportHeight = Math.max(40, sheetHeight - TITLE_AREA_HEIGHT - CLOSE_AREA_HEIGHT);

    contentContainer.x = sheetX + SHEET_PADDING;
    const contentHeight = buildCardTexts();
    maxScroll = Math.max(0, contentHeight - viewportHeight);
    scrollY = Phaser.Math.Clamp(scrollY, 0, maxScroll);
    contentContainer.y = viewportTop - scrollY;

    maskShape?.destroy();
    maskShape = scene.make.graphics({});
    maskShape.fillRect(sheetX, viewportTop, sheetWidth, viewportHeight);
    contentContainer.setMask(maskShape.createGeometryMask());

    dragZone.setPosition(sheetX, viewportTop);
    dragZone.setSize(sheetWidth, viewportHeight);
    dragZone.input && (dragZone.input.hitArea = new Phaser.Geom.Rectangle(0, 0, sheetWidth, viewportHeight));

    closeButton.setPosition(width / 2, sheetTop + sheetHeight - CLOSE_AREA_HEIGHT / 2);
  }

  relayout();

  return {
    container,
    setVisible(visible: boolean) {
      container.setVisible(visible);
      if (visible) {
        scrollY = 0;
        relayout();
      }
    },
    relayout,
    destroy() {
      scene.input.off("pointermove", onPointerMove);
      scene.input.off("pointerup", onPointerUp);
      scene.input.off("pointerupoutside", onPointerUp);
      scene.input.off("wheel", onWheel);
      maskShape?.destroy();
      container.destroy();
    },
  };
}
